import { useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useStore } from '../../store/store';
import useContactsDownloadPreview from '../../hooks/useContactsDownloadPreview';
import useEnrichmentContactsDownload from '../../hooks/useEnrichmentContactsDownload';
import { requestEnrichmentExportViaUrl } from '../profiler/CompanyDetails/utils/enrichmentContactsDownload';
import EnrichmentDownloadDialog from './EnrichmentDownloadDialog';

const PROJECT_CONTACTS_EXPORT_PATH = '/export-contacts-list';
const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/**
 * Project-scope connector for the v3 XLSX download dialog (TD-2101).
 *
 * Differs from the company connector in two ways (verified against
 * td-backend/develop):
 *  - the export endpoint is `/export-contacts-list?projectId=...`, and
 *  - on a 200 it returns `{ url }` (S3 pre-signed) rather than the binary, so
 *    it uses the `requestEnrichmentExportViaUrl` strategy.
 *
 * On open it fetches the merged download preview (useContactsDownloadPreview:
 * POST /bulk-download-precheck with includeContacts) — the same endpoint the
 * bulk-export confirmation uses, so the dialog shows BOTH the company-download
 * counter (A/B) and the contact count / cached credits (TD-2270 / TD-2271). The
 * fetch is fail-open: a precheck blip degrades the counters but never blocks a
 * legitimate download (the export endpoint re-checks the limit server-side).
 *
 * The 202 (Surfe job) + 402 (insufficient credits) paths are shared with the
 * company scope. Opened only for enrichment-enabled users (gate in
 * SearchResults — TD-1908 regression guard).
 *
 * AC-8 clause 5: on failed job, the dialog also closes (onError → handleClose).
 */
const ProjectContactsDownloadDialog = observer(function ProjectContactsDownloadDialog({
  isOpen,
  onClose,
  projectId,
  companyCount
}) {
  const { getToken, makeRequest } = useStore();
  const { preview, dialogProps } = useContactsDownloadPreview({
    isOpen,
    source: 'project',
    sourceId: projectId,
    makeRequest
  });

  // Late-bound reset reference. See CompanyContactsDownloadDialog for the
  // chicken-and-egg rationale.
  const resetRef = useRef(() => {});

  const handleClose = useCallback(() => {
    resetRef.current();
    onClose();
  }, [onClose]);

  const download = useEnrichmentContactsDownload({
    getToken,
    resolveBaseUrl: () => {
      const url = new URL(`${import.meta.env.VITE_API_BASEURL}${PROJECT_CONTACTS_EXPORT_PATH}`);
      url.searchParams.set('projectId', projectId);
      return url.toString();
    },
    filename: 'transact-digital_contacts.xlsx',
    contentType: XLSX_CONTENT_TYPE,
    linkId: 'download-project-contacts-btn',
    requestExport: requestEnrichmentExportViaUrl,
    onComplete: onClose,
    onError: handleClose
  });
  resetRef.current = download.reset;

  return (
    <EnrichmentDownloadDialog
      isOpen={isOpen}
      onClose={handleClose}
      variant="project"
      companyCount={preview?.companyCount ?? companyCount}
      {...dialogProps}
      phase={download.phase}
      isDownloading={download.isDownloading}
      enrichedCount={download.enrichedCount}
      totalContacts={download.totalContacts}
      onDownload={download.startDownload}
    />
  );
});

ProjectContactsDownloadDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  companyCount: PropTypes.number
};

export default ProjectContactsDownloadDialog;
